import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supportTicketService } from "../../services/supportTicketService";
import { formatDateTime } from "../../utils/dateFormat";
import { useBusy } from "../../context/BusyContext";
import { useMessageModal } from "../../admin/contexts/MessageModalContext";
import type { SupportTicket, TicketMessage } from "../../types/supportTicket";
import TicketStatusBadge from "../../components/support/TicketStatusBadge";
import TicketPriorityBadge from "../../components/support/TicketPriorityBadge";
import TicketConversation from "../../components/support/TicketConversation";
import TicketReplyForm from "../../components/support/TicketReplyForm";
import "../../assets/supportTickets.css";

const SubscriberTicketDetail: React.FC = () => {
  const { id } = useParams();
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [loadingReply, setLoadingReply] = useState(false);
  const { showModal } = useMessageModal();
  const { runBusy } = useBusy();

  const fetchDetail = async () => {
    if (!id) return;

    try {
      const [ticketRes, messageRes] = await runBusy(() =>
        Promise.all([
          supportTicketService.getTicketById(id),
          supportTicketService.getTicketMessages(id),
        ]),
      );

      setTicket(ticketRes);
      setMessages(messageRes || []);
    } catch (error) {
      console.error("Failed to fetch ticket detail", error);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const handleReply = async (payload: { message: string; files?: File[] }) => {
    if (!id) return;

    try {
      setLoadingReply(true);
      await supportTicketService.replyToTicket(id, payload);
      await fetchDetail();
    } catch (error) {
      console.error("Reply failed", error);
      if (error) {
        showModal({
          type: "error",
          title: "Failed",
          message: "Failed to create support ticket.",
        });
      }
    } finally {
      setLoadingReply(false);
    }
  };

  if (!ticket) {
    return (
      <div className="support-page-wrap">
        <div className="support-card">Loading ticket detail...</div>
      </div>
    );
  }

  return (
    <div className="support-page-wrap">
      <div className="support-card">
        <div className="ticket-detail-header">
          <div>
            <h2>{ticket.subject}</h2>
            <p>Ticket No: {ticket.ticketNo}</p>
          </div>

          <div className="ticket-detail-badges">
            <Link to="/support" className="open-ticket-btn">
              Your Tickets
            </Link>
            <TicketStatusBadge status={ticket.status} />
            <TicketPriorityBadge priority={ticket.priority} />
          </div>
        </div>

        <div className="ticket-meta-grid">
          <div>
            <strong>Department:</strong> {ticket.department}
          </div>
          <div>
            <strong>Service:</strong> {ticket.service}
          </div>
          <div>
            <strong>Created At:</strong> {formatDateTime(ticket.createdAt)}
          </div>
          <div>
            <strong>Assigned To:</strong>{" "}
            {ticket.assignedAdminName || "Not Assigned"}
          </div>
        </div>

        <div className="ticket-main-message">
          <h4>Ticket Description</h4>
          <p>{ticket.message}</p>
        </div>

        <TicketConversation messages={messages} />

        {ticket.status !== "Closed" && ticket.status !== "Resolved" && (
          <div className="ticket-reply-block">
            <h4 className="reply-block-title">Reply to Support</h4>
            <TicketReplyForm
              onSubmit={handleReply}
              loading={loadingReply}
              placeholder="Write your reply to support..."
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriberTicketDetail;
